import { Api } from '../../axios-config/AxiosConfig';
import { ApiException } from '../ApiException';
import { Environment } from '../../../environment';

export interface C4UsuariosSistema {
  id: string;
  sistemaId: string;
  usuarioId: string;
  container: number;
  acao: string;
  meioAcesso: string;
  usuario: { nome: string; descricao: string; id: string };
}

// Interfaces auxiliares para melhorar a legibilidade
interface Equipe {
  id: string;
  nome: string;
}

export interface AcessoContainer {
  id: string;
  containerId: string;
  tipo: number;
  meioAcesso: string;
}

export interface AcessoComponentes {
  id: string;
  nome: string;
}

export interface Container {
  id: string;
  sistemaId: string;
  tipo: number;
  linguagem: number;
  versao: string;
  acessoContainers: AcessoContainer[];
  acessoComponentes: AcessoComponentes[];
}


interface Integracao {
  id: string;
  sistemaId: string;
  sistemaIntegracaoId: string;
  tipoIntegracao: string; // 'SAAS' | 'ONPREMISE'
  descricao: string;
  sistemaIntegracao: C4Sistemas | null;
  acessoContainers: Array<{
    id: string;
    integracaoId: string;
    containerOrigem: number;
    containerDestino: number;
    acao: string;
    meioAcesso: string;
    acessoComponentes: Array<{
      id?: string; // optional for frontend display
      nome: string; // required for backend
    }>;
  }>;
}

// Tipo para envio do payload conforme swagger
export interface IntegracaoPayload {
  sistemaIntegracaoId: string;
  tipoIntegracao: string;
  descricao: string;
  acessoContainers: Array<{
    containerOrigem: string;
    containerDestino: string;
    acao: string;
    meioAcesso: string;
    acessoComponentes: Array<{
      nome: string;
    }>;
  }>;
}

export type { Integracao };

interface IntegracaoTerceiro {
  id: string;
  nomeSistema: string;
  sistemaIntegracaoId?: string;
  descricao: string;
  sistemaIntegracao: string;
  acessoContainers: Array<{
    id: string;
    integracaoTerceiroId: string;
    container: number;
    acao: string;
    meioAcesso: string;
    integracaoTerceiro: string;
  }> | null;
}

// Interface para payload de criação/edição (sem IDs)
interface IntegracaoTerceiroPayload {
  nomeSistema: string;
  sistemaIntegracaoId?: string;
  descricao: string;
  acessoContainers: Array<{
    container: number;
    acao: string;
    meioAcesso: string;
  }> | null;
}

export type { IntegracaoTerceiro, IntegracaoTerceiroPayload };

interface BancoDeDados {
  id?: string;
  sistemaId?: string;
  tipo: number;
  acessoContainers: Array<{
    id?: string;
    bancoDeDadosId?: string;
    tipo: number;
    meioAcesso: string;
    acessoComponentes: Array<{
      id?: string;
      nome: string;
    }>;
  }>;
}

export type { BancoDeDados };

interface Mensageria {
  id: string;
  sistemaId: string;
  nomeTopico: string;
  acessoContainers: Array<{
    id: string;
    mensageriaId: string;
    tipo: number;
    direcao: number;
  }>;
}

interface Camada {
  id: string;
  sistemaId: string;
  nome: string;
  container: number;
  sistema: string;
  componentes: Array<{
    id: string;
    camadaId: string;
    nome: string;
    camada: string;
    acessoComponentes: any[];
    acessoSistemas: any[];
  }>;
}

export type { Camada };

export interface C4Sistemas {
  id: string;
  nome: string;
  descricao: string;
  equipeId: string;
  usuariosSistema: C4UsuariosSistema[];
  containers: Container[];
  integracoes: Integracao[];
  integracoesTerceiro: IntegracaoTerceiro[];
  bancosDeDados: BancoDeDados[];
  mensagerias: Mensageria[];
  camadas: Camada[];
  equipe: Equipe;
}

// Interface para payload de criação/edição de sistemas
export interface C4SistemasPayload {
  id?: string;
  nome: string;
  descricao: string;
  equipeId: string;
  usuariosSistema: C4UsuariosSistema[];
  containers: Container[];
  integracoes: IntegracaoPayload[];
  integracoesTerceiro: IntegracaoTerceiroPayload[];
  bancosDeDados: BancoDeDados[];
  mensagerias: Mensageria[];
  camadas: Camada[];
  equipe: Equipe;
}

const create = async (
  dados: C4SistemasPayload
): Promise<string | number> => {
  try {
    const { data } = await Api.post(
      `${Environment.API_GATEWAY}/arquiteturac4model/api/v1/Sistemas`,
      dados,
      {
        headers: {
          idTenant: 'gvdasa-cloud',
        },
      }
    );
    return data as string | number;
  } catch (error: any) {
    throw new ApiException(
      error.data.data.errors.erro || 'Erro ao salvar o usuário'
    );
  }
};

const getAll = async (
  page: string,
  limite = 25,
  offset = 0,
  NomeParte?: string,
  order?: string,
  sortBy?: string
): Promise<{ data: C4Sistemas[]; totalCount: number }> => {
  try {
    // Construindo a query string com os parâmetros
    let queryParams = `?page=${page}&limite=${limite}&offset=${offset}`;

    // Adiciona filtro se existir
    if (NomeParte && NomeParte.trim()) {
      queryParams += `&NomeParte=${encodeURIComponent(NomeParte)}`;
    }

    // Adiciona ordenação se existir
    if (sortBy && order) {
      queryParams += `&sortBy=${sortBy}&order=${order}`;
    }

    const { data, headers } = await Api.get(
      `${Environment.API_GATEWAY}/arquiteturac4model/api/v1/Sistemas${queryParams}`,
      {
        headers: {
          idTenant: 'gvdasa-cloud',
        },
      }
    );

    return {
      data: data as C4Sistemas[],
      totalCount: Number(headers['x-total-count']),
    };

  } catch (error: any) {
    console.error('Erro ao consultar usuários:', error);
    throw new ApiException(
      error.data.data.erro || 'Erro ao consultar usuários.'
    );
  }
};

const getById = async (
  id: string | number
): Promise<C4Sistemas> => {
  try {
    const { data } = await Api.get(
      `${Environment.API_GATEWAY}/arquiteturac4model/api/v1/Sistemas/${id}`,
      {
        headers: {
          idTenant: 'gvdasa-cloud',
        },
      }
    );
    return data as C4Sistemas;
  } catch (error: any) {
    console.error(error);
    throw new ApiException(
      error.data.data.erro || 'Erro ao buscar os dados do usuário'
    );
  }
};

const deleteById = async (
  id: string | number
): Promise<void> => {
  try {
    await Api.delete(
      `${Environment.API_GATEWAY}/arquiteturac4model/api/v1/Sistemas/${id}`,
      {
        headers: {
          idTenant: 'gvdasa-cloud',
        },
      }
    );
    return;
  } catch (error: any) {
    console.error(error);
    throw new ApiException(
      error.data.data.erro || 'Erro ao excluir o usuário'
    );
  }
};

const updateById = async (
  id: string | number,
  dados: C4SistemasPayload
): Promise<void> => {
  try {
    await Api.put(
      `${Environment.API_GATEWAY}/arquiteturac4model/api/v1/Sistemas/${id}`,
      dados,
      {
        headers: {
          idTenant: 'gvdasa-cloud',
        },
      }
    );
    return;
  } catch (error: any) {
    throw new ApiException(error.data.data.erro || 'Erro ao salvar o usuário');
  }
};

export const C4ModelSistemasService = {
  create,
  getAll,
  getById,
  updateById,
  deleteById,
};
